import { DeleteOutlined, FileExcelOutlined, FilePdfOutlined, SearchOutlined } from '@ant-design/icons';
import { Breadcrumb, Button, Checkbox, Col, Dropdown, Input, Menu, Pagination, Popconfirm, Row, Select, Space, Table, Tooltip, message } from 'antd';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import moment from 'moment';
import { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { ReactComponent as Back } from '../../Images/Back.svg';
import { ReactComponent as ColumnList } from '../../Images/ColumnList.svg';
import { ReactComponent as EditIcon } from '../../Images/EditIcon.svg';
import { ReactComponent as FilterIcon } from '../../Images/FilterIcon.svg';
import { archivePlanning, fetchPlanningListByField, resetPlanningList } from '../../slices/Planning';
import dayjs from 'dayjs';
import { createActivityLog } from '../../slices/activity-log';
import { fetchAndSetIpAddress } from '../../slices/ipSlice';
import * as XLSX from 'xlsx';
import { fetchDiePressList } from '../../slices/die-press';
import eventBus from '../../utils/eventBus';
import logo from '../../Images/alux-logo.png';

const { Option } = Select;

function PlanningList() {
  let navigate = useNavigate();
  const dispatch = useDispatch();
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const { user: currentUser } = useSelector(state => state.auth);

  const { data = [], totalRecord, status } = useSelector(state => state.Planning);
  const [search, setSearch] = useState(localStorage.getItem('PlanningList') || '');
  const { data: pressList = [] } = useSelector(state => state.diePress);
  const [inputValue, setInputValue] = useState(localStorage.getItem('PlanningList') || '');
  const [orderBy, setOrderBy] = useState('');
  const [selectedPressID, setSelectedPressID] = useState(null);
  const ipAddress = useSelector(state => state.ip.ipAddress);

  useEffect(() => {
    if (ipAddress === null) {
      dispatch(fetchAndSetIpAddress());
    }
  }, [dispatch, ipAddress]);

  const [page, setPage] = useState(1);
  const [pagesize, setPageSize] = useState(20);
  const [checkedList, setCheckedList] = useState([
    'SrNo',
    'planning_number',
    'profile_image',
    'planning_date',
    'customer_name',
    'iwo_no_date',
    'profile',
    'press',
    'cut_length',
    'plan_pcs',
    'plan_qty',
    'grade',
    'alloy_temper',
    'quenching_type',
    'aging',
    'std_kg_m',
    'last_kg_m',
    'process_remarks',
    'action',
  ]);

  useEffect(() => {
    dispatch(
      fetchDiePressList({
        page: 1,
        pagesize: 100,
      }),
    );
  }, [dispatch]);

  const handleColumnVisibilityChange = checkedValues => {
    setCheckedList(checkedValues);
  };

  const handleEdit = data => {
    navigate(`/edit-planning/${data.id}`);
  };

  const handleDelete = id => {
    if (!id) {
      message.error('Missing ID for deletion!');
      return;
    }
    dispatch(archivePlanning(id))
      .unwrap()
      .then(() => {
        message.success('Planning Deleted Successfully!');
        dispatch(fetchPlanningListByField({ page, pagesize }));
      })
      .catch(error => {
        if (typeof error === 'object' && error !== null) {
          const messages = [];
          for (const key in error) {
            if (Array.isArray(error[key])) {
              messages.push(`${key}: ${error[key].join(', ')}`);
            }
          }
          if (messages.length > 0) {
            message.error(messages.join('; '));
          } else {
            message.error('An unknown error occurred.');
          }
        } else if (typeof error === 'string') {
          message.error(error);
        } else if (error instanceof Error) {
          message.error(error.message);
        } else {
          message.error('An unknown error occurred.');
        }
      });
  };

  const columns = [
    {
      title: 'Sr No',
      dataIndex: 'SrNo',
      key: 'SrNo',
      width: 80,
      render: (text, record, index) => {
        return (page - 1) * pagesize + index + 1;
      },
    },
    {
      title: 'Planning No',
      dataIndex: 'planning_number',
      key: 'planning_number',
      sorter: true,
      render: (text, record) => {
        return <span>{record?.planning_number || 'N/A'}</span>;
      },
    },
    {
      title: 'Planning Date',
      dataIndex: 'planning_date',
      key: 'planning_date',
      sorter: true,
      render: planning_date => {
        return (
          <span>
            {planning_date ? dayjs(planning_date).format('DD-MM-YYYY') : 'N/A'}
          </span>
        );
      },
    },
    {
      title: 'Customer Name',
      dataIndex: 'customer_name',
      key: 'customer_name',
      render: (text, record) => {
        return <span>{record?.customer_name || 'N/A'}</span>;
      },
    },
    {
      title: 'IWO No / Date',
      dataIndex: 'iwo_no_date',
      key: 'iwo_no_date',
      render: (text, record) => {
        const workorderNo = record?.workorder_no || '';
        const workorderDate = record?.workorder_date
          ? dayjs(record.workorder_date).format('DD-MM-YYYY')
          : '';
        return <span>{workorderNo && workorderDate ? `${workorderNo} / ${workorderDate}` : 'N/A'}</span>;
      },
    },
    {
      title: 'Profile Image',
      dataIndex: 'profile_image',
      key: 'profile_image',
      render: record => {
        const fileUrl = record;

        const imageContainerStyle = {
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          borderRadius: '4px',
          overflow: 'hidden',
        };

        const imageStyle = {
          width: '90px',
          height: '90px',
          maxWidth: '100%',
          maxHeight: '100%',
          objectFit: 'contain',
          display: 'block',
        };

        if (!fileUrl) {
          return (
            <div style={imageContainerStyle}>
              <span style={{ fontSize: '12px', color: '#999' }}>N/A</span>
            </div>
          );
        }

        const fileExtension = fileUrl.split('.').pop().toLowerCase();

        if (fileExtension === 'pdf') {
          return (
            <Button
              type="link"
              style={{ padding: '0px' }}
              onClick={async () => {
                const response = await fetch(fileUrl);
                if (!response.ok) {
                  throw new Error(`HTTP error! Status: ${response.status}`);
                }
                const blobData = await response.blob();
                const blob = new Blob([blobData], { type: 'application/pdf' });
                const blobURL = URL.createObjectURL(blob);
                window.open(blobURL);
              }}
            >
              View PDF
            </Button>
          );
        } else if (
          ['jpg', 'jpeg', 'png', 'gif', 'bmp'].includes(fileExtension)
        ) {
          return (
            <div style={imageContainerStyle}>
              <img src={fileUrl} alt="Die Diagram" style={imageStyle} />
            </div>
          );
        } else {
          return (
            <div style={imageContainerStyle}>
              <span style={{ fontSize: '12px', color: '#999' }}>Unsupported</span>
            </div>
          );
        }
      },
    },
    {
      title: 'Profile No',
      dataIndex: 'profile',
      key: 'profile',
      render: (text, record) => {
        const profile = record?.profile;
        if (typeof profile === 'object' && profile !== null) {
          return <span>{profile?.die_number || profile?.name || 'N/A'}</span>;
        }
        return <span>{profile || 'N/A'}</span>;
      },
    },
    {
      title: 'Press No',
      dataIndex: 'press',
      key: 'press',
      render: (text, record) => {
        const press = record?.press;
        if (typeof press === 'object' && press !== null) {
          return <span>{press?.name || press?.press_number || press?.press_no || 'N/A'}</span>;
        }
        return <span>{press || 'N/A'}</span>;
      },
    },
    {
      title: 'Cut Length',
      dataIndex: 'cut_length',
      key: 'cut_length',
      render: (text, record) => {
        return <span>{record?.length || 'N/A'}</span>;
      },
    },
    {
      title: 'Planning Pcs',
      dataIndex: 'plan_pcs',
      key: 'plan_pcs',
      sorter: true,
      render: plan_pcs => {
        return <span>{plan_pcs || 'N/A'}</span>;
      },
    },
    {
      title: 'Planning Kgs',
      dataIndex: 'plan_qty',
      key: 'plan_qty',
      sorter: true,
      render: plan_qty => {
        return <span>{plan_qty || 'N/A'}</span>;
      },
    },
    {
      title: 'Grade',
      dataIndex: 'grade',
      key: 'grade',
      render: (text, record) => {
        return <span>{record?.grade || 'N/A'}</span>;
      },
    },
    {
      title: 'Alloy',
      dataIndex: 'alloy_temper',
      key: 'alloy_temper',
      render: (text, record) => {
        return <span>{record?.alloy_temper || 'N/A'}</span>;
      },
    },
    {
      title: 'Quenching',
      dataIndex: 'quenching_type',
      key: 'quenching_type',
      render: quenching_type => {
        return <span>{quenching_type || 'N/A'}</span>;
      },
    },
    {
      title: 'Ageing',
      dataIndex: 'aging',
      key: 'aging',
      render: aging => {
        return <span>{aging || 'N/A'}</span>;
      },
    },
    {
      title: 'STD. Kg/M',
      dataIndex: 'std_kg_m',
      key: 'std_kg_m',
      render: (text, record) => {
        return <span>{record?.standard_wt_kg_p_mt || 'N/A'}</span>;
      },
    },
    {
      title: 'Last Kg/M',
      dataIndex: 'last_kg_m',
      key: 'last_kg_m',
      render: (text, record) => {
        return <span>{record?.last_wt_kg_p_mt || 'N/A'}</span>;
      },
    },
    {
      title: 'Process Remarks',
      dataIndex: 'process_remarks',
      key: 'process_remarks',
      render: (text, record) => {
        return <span>{record?.remarks || 'N/A'}</span>;
      },
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 100,
      render: record => {
        return (
          <span>
            {record ? dayjs.utc(record).local().format('DD-MM-YYYY hh:mm A') : 'N/A'}
          </span>
        );
      },
    },
    {
      title: 'Created By',
      dataIndex: 'created_by',
      key: 'created_by',
      width: 100,
      render: record => {
        return (
          <span>
            {record ? record : 'N/A'}
          </span>
        );
      },
    },
    {
      title: 'Updated At',
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 100,
      render: record => {
        return (
          <span>
            {record && record.trim()
              ? dayjs(record).format('DD-MM-YYYY HH:mm')
              : 'N/A'}
          </span>
        );
      },
    },
    {
      title: 'Updated By',
      dataIndex: 'updated_by',
      key: 'updated_by',
      width: 100,
      render: record => {
        return (
          <span>
            {record ? record : 'N/A'}
          </span>
        );
      },
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      render: (_, record) => (
        <Space size="middle">
          {currentUser.permissions.permissions.planning.includes(
            'change_planning',
          ) && (
              <Tooltip placement="top" title={'Edit'}>
                <span
                  onClick={() => handleEdit(record)}
                  style={{ cursor: 'pointer' }}
                >
                  <EditIcon />
                </span>
              </Tooltip>
            )}
          {currentUser.permissions.permissions.planning.includes(
            'delete_planning',
          ) && (
              <Link>
                <Tooltip placement="top" title={'Delete'}>
                  <Popconfirm
                    title="Are you sure you want to delete this record?"
                    onConfirm={() => handleDelete(record.id)}
                    onCancel={() => message.info('Action cancelled')}
                    okText="Yes"
                    cancelText="No"
                  >
                    <DeleteOutlined
                      style={{ cursor: 'pointer', fontSize: '18px' }}
                    />
                  </Popconfirm>
                </Tooltip>
              </Link>
            )}
        </Space>
      ),
    },
  ];

  const filteredColumns = columns.filter(column =>
    checkedList.includes(column.key),
  );

  const fetchPlaning = useCallback(() => {
    dispatch(
      fetchPlanningListByField({
        pressID: selectedPressID,
        page,
        pagesize,
        ...(search && { search: search }),
        ...(orderBy && { ordering: orderBy }),
      }),
    );
  }, [dispatch, page, pagesize, search, orderBy, selectedPressID]);

  useEffect(() => {
    dispatch(resetPlanningList());
    fetchPlaning();
  }, [fetchPlaning]);

  useEffect(() => {
    const handleReload = () => {
      fetchPlaning();
    };

    eventBus.on("financialYear:refresh", handleReload);

    return () => {
      eventBus.off("financialYear:refresh", handleReload);
    };
  }, [fetchPlaning]);

  const dataSource = data.map((item, index) => ({
    ...item,
    key: item.id || index,
  }));

  const handleTableChange = (pagination, filters, sorter) => {
    if (sorter?.columnKey && sorter.order) {
      const orderBy = GetSortingEnumValue(sorter.columnKey, sorter.order);
      setOrderBy(orderBy);
    }
  };

  const handlePageSizeChange = (current, size) => {
    setPage(1);
    setPageSize(size);
  };

  const handleSearch = e => {
    const value = e.target.value;
    setInputValue(value);
    setSearch(value);
    setPage(1); // Reset to first page when searching
    localStorage.setItem('PlanningList', value);
  };

  const resetFilter = () => {
    setSearch('');
    setInputValue('');
    setOrderBy(null);
    setSelectedPressID(null); // Reset selected press ID
    setPage(1); // Reset to first page when clearing filter
    localStorage.removeItem('PlanningList');
  };

  const handlePageChange = newPage => {
    setPage(newPage);
    setOrderBy('');
  };

  const GetSortingEnumValue = (sortField, sortOrder) => {
    let sortingEnumKey = 'id';

    if (sortField === 'planning_number' && sortOrder === 'ascend') {
      sortingEnumKey = 'planning_number';
    }
    if (sortField === 'planning_number' && sortOrder === 'descend') {
      sortingEnumKey = '-planning_number';
    }
    if (sortField === 'planning_date' && sortOrder === 'ascend') {
      sortingEnumKey = 'planning_date';
    }
    if (sortField === 'planning_date' && sortOrder === 'descend') {
      sortingEnumKey = '-planning_date';
    }
    if (sortField === 'plan_pcs' && sortOrder === 'ascend') {
      sortingEnumKey = 'plan_pcs';
    }
    if (sortField === 'plan_pcs' && sortOrder === 'descend') {
      sortingEnumKey = '-plan_pcs';
    }
    if (sortField === 'plan_qty' && sortOrder === 'ascend') {
      sortingEnumKey = 'plan_qty';
    }
    if (sortField === 'plan_qty' && sortOrder === 'descend') {
      sortingEnumKey = '-plan_qty';
    }

    return sortingEnumKey;
  };

  const columnMenu = (
    <Menu className="packagelistdropdown">
      <Menu.Item key="columnMenu">
        <Checkbox.Group
          value={checkedList}
          onChange={handleColumnVisibilityChange}
        >
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Checkbox value="SrNo" disabled>
                Sr No
              </Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="planning_number">Planning No</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="planning_date">Planning Date</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="customer_name">Customer Name</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="iwo_no_date">IWO No / Date</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="profile">Profile No</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="press">Press No</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="cut_length">Cut Length</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="plan_pcs">Planning Pcs</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="plan_qty">Planning Kgs</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="grade">Grade</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="alloy_temper">Alloy</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="quenching_type">Quenching</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="aging">Ageing</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="std_kg_m">STD. Kg/M</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="last_kg_m">Last Kg/M</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="process_remarks">Process Remarks</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="created_at">Created At</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="created_by">Created By</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="updated_at">Updated At</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="updated_by">Updated By</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="action" disabled>
                Action
              </Checkbox>
            </Col>
          </Row>
        </Checkbox.Group>
      </Menu.Item>
    </Menu>
  );

  const allCsvColumns = [
    { header: 'Sr No', dataKey: 'SrNo', field: 'SrNo' },
    { header: 'Planning No', dataKey: 'planning_number', field: 'planning_number' },
    { header: 'Planning Date', dataKey: 'planning_date', field: 'planning_date' },
    { header: 'Customer Name', dataKey: 'customer_name', field: 'customer_name' },
    { header: 'IWO No / Date', dataKey: 'iwo_no_date', field: 'iwo_no_date' },
    { header: 'Profile No', dataKey: 'profile', field: 'profile' },
    { header: 'Press No', dataKey: 'press', field: 'press' },
    { header: 'Cut Length', dataKey: 'cut_length', field: 'cut_length' },
    { header: 'Planning Pcs', dataKey: 'plan_pcs', field: 'plan_pcs' },
    { header: 'Planning Kgs', dataKey: 'plan_qty', field: 'plan_qty' },
    { header: 'Grade', dataKey: 'grade', field: 'grade' },
    { header: 'Alloy', dataKey: 'alloy_temper', field: 'alloy_temper' },
    { header: 'Quenching', dataKey: 'quenching_type', field: 'quenching_type' },
    { header: 'Ageing', dataKey: 'aging', field: 'aging' },
    { header: 'STD. Kg/M', dataKey: 'std_kg_m', field: 'std_kg_m' },
    { header: 'Last Kg/M', dataKey: 'last_kg_m', field: 'last_kg_m' },
    { header: 'Process Remarks', dataKey: 'process_remarks', field: 'process_remarks' },
    { header: 'Created At', dataKey: 'created_at', field: 'created_at' },
    { header: 'Created By', dataKey: 'created_by', field: 'created_by' },
    { header: 'Updated At', dataKey: 'updated_at', field: 'updated_at' },
    { header: 'Updated By', dataKey: 'updated_by', field: 'updated_by' },
  ];

  const csvListData = dataSource.map((item, index) => {
    const row = {};

    allCsvColumns
      .filter(col => checkedList.includes(col.field))
      .forEach(col => {
        switch (col.field) {
          case 'SrNo':
            row[col.dataKey] = index + 1;
            break;
          case 'planning_date':
            row[col.dataKey] = item?.planning_date ? moment(item.planning_date).format('DD-MM-YYYY') : 'N/A';
            break;
          case 'iwo_no_date':
            const workorderNo = item?.workorder_no || '';
            const workorderDate = item?.workorder_date
              ? moment(item?.workorder_date).format('DD-MM-YYYY')
              : '';
            row[col.dataKey] = workorderNo && workorderDate ? `${workorderNo} / ${workorderDate}` : 'N/A';
            break;
          case 'cut_length':
            row[col.dataKey] = item?.length || 'N/A';
            break;
          case 'std_kg_m':
            row[col.dataKey] = item?.standard_wt_kg_p_mt || 'N/A';
            break;
          case 'last_kg_m':
            row[col.dataKey] = item?.last_wt_kg_p_mt || 'N/A';
            break;
          case 'process_remarks':
            row[col.dataKey] = item?.remarks || 'N/A';
            break;
          case 'press':
            const press = item?.press;
            if (typeof press === 'object' && press !== null) {
              row[col.dataKey] = press?.name || press?.press_number || press?.press_no || 'N/A';
            } else {
              row[col.dataKey] = press || 'N/A';
            }
            break;
          case 'customer_name':
            row[col.dataKey] = item?.customer_name || 'N/A';
            break;
          case 'profile':
            const profile = item?.profile;
            if (typeof profile === 'object' && profile !== null) {
              row[col.dataKey] = profile?.die_number || profile?.name || 'N/A';
            } else {
              row[col.dataKey] = profile || 'N/A';
            }
            break;
          case 'alloy_temper':
            row[col.dataKey] = item?.alloy_temper || 'N/A';
            break;
          case 'grade':
            row[col.dataKey] = item?.grade || 'N/A';
            break;
          case 'created_at':
          case 'updated_at':
            row[col.dataKey] = item[col.field]
              ? moment(item[col.field]).format('DD-MM-YYYY')
              : 'N/A';
            break;
          case 'created_by':
          case 'updated_by':
            row[col.dataKey] = item[col.field] || 'N/A';
            break;
          default:
            row[col.dataKey] = item[col.field] || 'N/A';
            break;
        }
      });

    return row;
  });

  const loadImageAsBase64 = url => {
    return new Promise((resolve, reject) => {
      const cacheBustUrl = `${url}${url.includes('?') ? '&' : '?'}nocache=${new Date().getTime()}`;

      const img = new Image();
      img.crossOrigin = 'Anonymous';

      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/jpeg'));
      };

      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = cacheBustUrl;
    });
  };

  function getFileExtension(url) {
    return url.split('.').pop().split(/[#?]/)[0];
  }

  const handlePdfDownload = async () => {
    const imagePromises = data.map(async item => {
      if (!item?.profile_image) return { id: item.id, base64: null };

      const fileUrl = item?.profile_image;
      const extension = getFileExtension(fileUrl).toUpperCase();
      const validImageFormats = ['PNG', 'JPEG', 'JPG'];

      if (validImageFormats.includes(extension)) {
        try {
          const base64Image = await loadImageAsBase64(fileUrl);
          return { id: item.id, base64: base64Image, extension };
        } catch (error) {
          console.error(`Failed to load image for ${item?.profile_image}:`, error);
          return { id: item.id, base64: null };
        }
      }
      return { id: item.id, base64: null };
    });

    const loadedImages = await Promise.all(imagePromises);
    const imageMap = loadedImages.reduce((map, img) => {
      map[img.id] = img;
      return map;
    }, {});

    const pdf = new jsPDF('landscape', 'pt', 'a4');

    const logoWidth = 150;
    const logoHeight = 32;



    pdf.addImage(
      logo,
      'PNG',
      pdf.internal.pageSize.width - logoWidth - 20, // Right side with margin
      20,
      logoWidth,
      logoHeight
    );

    const pdfColumns = [
      { header: 'Sr No', dataKey: 'SrNo', field: 'SrNo' },
      { header: 'Planning No', dataKey: 'planning_number', field: 'planning_number' },
      { header: 'Planning Date', dataKey: 'planning_date', field: 'planning_date' },
      { header: 'Customer Name', dataKey: 'customer_name', field: 'customer_name' },
      { header: 'IWO No / Date', dataKey: 'iwo_no_date', field: 'iwo_no_date' },
      { header: 'Profile No', dataKey: 'profile', field: 'profile' },
      { header: 'Profile Image', dataKey: 'profile_image', field: 'profile_image', cellWidth: 80 },
      { header: 'Press No', dataKey: 'press', field: 'press' },
      { header: 'Cut Length', dataKey: 'cut_length', field: 'cut_length' },
      { header: 'Planning Pcs', dataKey: 'plan_pcs', field: 'plan_pcs' },
      { header: 'Planning Kgs', dataKey: 'plan_qty', field: 'plan_qty' },
      { header: 'Grade', dataKey: 'grade', field: 'grade' },
      { header: 'Alloy', dataKey: 'alloy_temper', field: 'alloy_temper' },
      { header: 'Quenching', dataKey: 'quenching_type', field: 'quenching_type' },
      { header: 'Ageing', dataKey: 'aging', field: 'aging' },
      { header: 'STD. Kg/M', dataKey: 'std_kg_m', field: 'std_kg_m' },
      { header: 'Last Kg/M', dataKey: 'last_kg_m', field: 'last_kg_m' },
      { header: 'Process Remarks', dataKey: 'process_remarks', field: 'process_remarks' },
      { header: 'Created At', dataKey: 'created_at', field: 'created_at' },
      { header: 'Created By', dataKey: 'created_by', field: 'created_by' },
      { header: 'Updated At', dataKey: 'updated_at', field: 'updated_at' },
      { header: 'Updated By', dataKey: 'updated_by', field: 'updated_by' },
    ];

    const columns = pdfColumns.filter(col => checkedList.includes(col.field));
    const rows = dataSource.map((item, index) => {
      const row = {};
      columns.forEach(col => {
        switch (col.field) {
          case 'SrNo':
            row.SrNo = index + 1;
            break;
          case 'planning_number':
            row.planning_number = item?.planning_number || 'N/A';
            break;
          case 'planning_date':
            row.planning_date = item?.planning_date ? dayjs(item.planning_date).format('DD-MM-YYYY') : 'N/A';
            break;
          case 'customer_name':
            row.customer_name = item?.customer_name || 'N/A';
            break;
          case 'iwo_no_date':
            const workorderNo = item?.workorder_no || '';
            const workorderDate = item?.workorder_date
              ? dayjs(item.workorder_date).format('DD-MM-YYYY')
              : '';
            row[col.dataKey] = workorderNo && workorderDate ? `${workorderNo} / ${workorderDate}` : 'N/A';
            break;
          case 'profile_image': {
            const fileUrl = item?.profile_image;
            if (!fileUrl) {
              row.profile_image = ''; // no text
              break;
            }
            const fileExtension = fileUrl.split('.').pop().toLowerCase();
            if (['jpg', 'jpeg', 'png', 'gif', 'bmp'].includes(fileExtension)) {
              row.profile_image = ''; // leave empty, image will be drawn in didDrawCell
            } else if (fileExtension === 'pdf') {
              row.profile_image = 'Download PDF'; // optional, keep only for PDFs
            } else {
              row.profile_image = 'Unsupported';
            }
            break;
          }
          case 'profile':
            const profileForPdf = item?.profile;
            if (typeof profileForPdf === 'object' && profileForPdf !== null) {
              row.profile = profileForPdf?.die_number || profileForPdf?.name || 'N/A';
            } else {
              row.profile = profileForPdf || 'N/A';
            }
            break;
          case 'press':
            const pressForPdf = item?.press;
            if (typeof pressForPdf === 'object' && pressForPdf !== null) {
              row.press = pressForPdf?.name || pressForPdf?.press_number || pressForPdf?.press_no || 'N/A';
            } else {
              row.press = pressForPdf || 'N/A';
            }
            break;
          case 'cut_length':
            row.cut_length = item?.length || 'N/A';
            break;
          case 'alloy_temper':
            row.alloy_temper = item?.alloy_temper || 'N/A';
            break;
          case 'quenching_type':
            row.quenching_type = item?.quenching_type || 'N/A';
            break;
          case 'aging':
            row.aging = item?.aging || 'N/A';
            break;
          case 'grade':
            row.grade = item?.grade || 'N/A';
            break;
          case 'plan_pcs':
            row.plan_pcs = item?.plan_pcs || 'N/A';
            break;
          case 'plan_qty':
            row.plan_qty = item?.plan_qty || 'N/A';
            break;
          case 'std_kg_m':
            row.std_kg_m = item?.standard_wt_kg_p_mt || 'N/A';
            break;
          case 'last_kg_m':
            row.last_kg_m = item?.last_wt_kg_p_mt || 'N/A';
            break;
          case 'process_remarks':
            row.process_remarks = item?.remarks || 'N/A';
            break;
          case 'created_at':
            row.created_at = item?.created_at ? dayjs(item.created_at).format('DD-MM-YYYY') : 'N/A';
            break;
          case 'created_by':
            row.created_by = item?.created_by || 'N/A';
            break;
          case 'updated_at':
            row.updated_at = item?.updated_at ? dayjs(item.updated_at).format('DD-MM-YYYY') : 'N/A';
            break;
          case 'updated_by':
            row.updated_by = item?.updated_by || 'N/A';
            break;
          default:
            row[col.dataKey] = 'N/A';
        }
      });
      return row;
    });

    pdf.text('Extrusion Production Planning', pdf.internal.pageSize.width / 2, 40, { align: 'center' });

    pdf.autoTable(columns, rows, {
      startY: 80,
      theme: 'grid',
      styles: {
        font: 'times',
        halign: 'center',
        overflow: 'linebreak',
        cellPadding: 4.5,
        lineWidth: 0.5,
        lineColor: [0, 0, 0],
        textColor: [0, 0, 0],
      },
      headStyles: {
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        lineWidth: 0.5,
        lineColor: [0, 0, 0],
        fillColor: false,
      },
      rowStyles: {
        lineWidth: 0.5,
        lineColor: [0, 0, 0],
      },
      tableLineColor: [0, 0, 0],
      rowPageBreak: 'avoid',
      didParseCell: function (data) {
        if (data.column.dataKey === 'profile_image' && data.cell.section === 'body') {
          data.cell.styles.minCellHeight = 50; // keep row tall enough for image
        }
      },
      didDrawCell: function (row) {
        if (row.column.dataKey === 'profile_image' && row.cell.section === 'body') {
          const item = data[row.row.index];
          if (!item) return;

          const fileUrl = item?.profile_image;
          const extension = fileUrl ? getFileExtension(fileUrl).toUpperCase() : 'N/A';
          const validImageFormats = ['PNG', 'JPEG', 'JPG'];

          if (validImageFormats.includes(extension) && imageMap[item.id]?.base64) {
            const imgWidth = 45;
            const imgHeight = 45;

            const imgX = row.cell.x + (row.cell.width - imgWidth) / 2;
            const imgY = row.cell.y + (row.cell.height - imgHeight) / 2;

            // Draw the image
            pdf.addImage(imageMap[item.id].base64, extension, imgX, imgY, imgWidth, imgHeight);

            // 🔹 Redraw the cell border so the line is not hidden by image
            pdf.setDrawColor(0, 0, 0);
            pdf.setLineWidth(0.5);
            pdf.rect(row.cell.x, row.cell.y, row.cell.width, row.cell.height);
          } else {
            const linkText = fileUrl ? 'Download File' : 'N/A';
            const linkX = row.cell.x + (row.cell.width - pdf.getTextWidth(linkText)) / 2;
            const linkY = row.cell.y + row.cell.height / 2 + 3;
            pdf.setTextColor(0, 0, 255);
            pdf.textWithLink(linkText, linkX, linkY, { url: fileUrl });
            pdf.setTextColor(0, 0, 0);

            // 🔹 Also redraw border here
            pdf.setDrawColor(0, 0, 0);
            pdf.setLineWidth(0.5);
            pdf.rect(row.cell.x, row.cell.y, row.cell.width, row.cell.height);
          }
        }
      },
      didDrawPage: data => {
        const pageNumber = pdf.internal.getCurrentPageInfo().pageNumber;
        const totalPages = pdf.internal.getNumberOfPages();

        const now = dayjs().format('DD-MM-YYYY HH:mm');
        pdf.setFontSize(9);
        pdf.text(now, 40, pdf.internal.pageSize.height - 20);

        pdf.text(
          `Page ${pageNumber} of ${totalPages}`,
          pdf.internal.pageSize.width - 100,
          pdf.internal.pageSize.height - 20
        );
      },
    });

    pdf.save(`Extrusions Production PlanningList${+new Date()}.pdf`);

    dispatch(
      createActivityLog({
        action: 'DOWNLOAD',
        module_name: 'Planning',
        ip_address: ipAddress,
        discription: 'Planning pdf download successfully!',
      })
    );
  };


  const handleExcelDownload = async () => {
    const wb = XLSX.utils.book_new();
    const wsData = [];
    wsData.push([{ v: 'EXTRUSIONS Production Planning', t: 's', s: { font: { bold: true, sz: 16 } } }]);
    wsData.push([]);
    const filteredColumns = allCsvColumns.filter(col => checkedList.includes(col.field));
    const headerRow1 = [];
    const headerRow2 = [];

    filteredColumns.forEach(col => {
      if (col.field === 'plan_pcs' || col.field === 'plan_qty') {
        if (col.field === 'plan_pcs') {
          headerRow1.push('Planning');
          headerRow2.push('Pcs');
        } else {
          headerRow1.push('');
          headerRow2.push('Kgs');
        }
      } else {
        headerRow1.push(col.header);
        headerRow2.push('');
      }
    });

    wsData.push(headerRow1);
    wsData.push(headerRow2);
    const dataRows = csvListData.map(item => {
      const row = [];
      filteredColumns.forEach(col => {
        row.push(item[col.dataKey] || 'N/A');
      });
      return row;
    });
    wsData.push(...dataRows);
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    const totalColumns = filteredColumns.length;
    if (totalColumns > 0) {
      const headingMergeRange = {
        s: { r: 0, c: 0 },
        e: { r: 0, c: totalColumns - 1 }
      };
      if (!ws['!merges']) ws['!merges'] = [];
      ws['!merges'].push(headingMergeRange);
    }
    const planningStartCol = filteredColumns.findIndex(col => col.field === 'plan_pcs');
    const planningEndCol = filteredColumns.findIndex(col => col.field === 'plan_qty');
    if (planningStartCol !== -1 && planningEndCol !== -1) {
      const mergeRange = {
        s: { r: 2, c: planningStartCol },
        e: { r: 2, c: planningEndCol }
      };
      if (!ws['!merges']) ws['!merges'] = [];
      ws['!merges'].push(mergeRange);
    }

    ws['!cols'] = filteredColumns.map(() => ({ wch: 15 }));
    XLSX.utils.book_append_sheet(wb, ws, 'PlanningList');
    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([wbout], { type: 'application/octet-stream' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    const now = new Date();

    // Format date & time (e.g. 2025-08-31_15-42-10)
    const formattedDateTime = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}_${String(now.getHours()).padStart(2, "0")}-${String(now.getMinutes()).padStart(2, "0")}-${String(now.getSeconds()).padStart(2, "0")}`;

    link.setAttribute('download', `EXTRUSIONS_Production_Planning_${formattedDateTime}.xlsx`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    dispatch(
      createActivityLog({
        action: 'DOWNLOAD',
        module_name: 'Planning',
        ip_address: ipAddress,
        discription: 'Planning excel download successfully!',
      })
    );
  };

  return (
    <div className='mainwrap'>
      <div className="sticyaction">
        <div className="sticky-header">
          <Row
            className="pagenamerow mb-0 "
            justify="space-between"
            align="middle"
          >
            <Col>
              <h2 style={{ margin: 0 }}>Planning</h2>
              <div className="bredcrumbwrp">
                <Link onClick={() => navigate(-1)} className="back">
                  <Back /> BACK
                </Link>
                <Breadcrumb
                  items={[
                    { title: <Link to="/dashboard">Home</Link> },
                    { title: 'Planning' },
                  ]}
                />
              </div>
            </Col>
            <Col>
              <Space size="small">
                <Select
                  placeholder="Select Press"
                  style={{ width: 250 }}
                  showSearch
                  allowClear
                  optionFilterProp="children"
                  onChange={value => {
                    setSelectedPressID(value);
                    setPage(1);
                  }}
                  value={selectedPressID}
                >
                  {pressList.map(item => (
                    <Option key={item.id} value={item.id}>
                      {item.name || 'N/A'}
                    </Option>
                  ))}
                </Select>
                <Input
                  placeholder="Search"
                  className="searchbox"
                  prefix={<SearchOutlined />}
                  value={inputValue}
                  style={{ width: 190 }}
                  onChange={handleSearch}
                />
                <Tooltip title="Filter">
                  <Button
                    type="primary"
                    disabled={!search && !orderBy && !selectedPressID}
                    className="iconlink"
                    onClick={resetFilter}
                  >
                    <FilterIcon />
                  </Button>
                </Tooltip>
                {currentUser.permissions.permissions.planning.includes(
                  'download_planning_excel_copy',
                ) && (
                    <Tooltip placement="top" title={'Export Excel'}>
                      <Button
                        type="primary"
                        className="iconlink"
                        onClick={handleExcelDownload}
                      >
                        <FileExcelOutlined />
                      </Button>
                    </Tooltip>
                  )}
                {currentUser.permissions.permissions.planning.includes(
                  'download_planning_pdf_copy',
                ) && (
                    <Tooltip placement="top" title={'Export PDF'}>
                      <Button
                        type="primary"
                        className="iconlink"
                        onClick={handlePdfDownload}
                      >
                        <FilePdfOutlined />
                      </Button>
                    </Tooltip>
                  )}
                {currentUser.permissions.permissions.planning.includes(
                  'add_planning',
                ) && (
                    <Link to="/Priority-list">
                      <Tooltip title="Production Planning">
                        <Button type="primary">Create Planning</Button>
                      </Tooltip>
                    </Link>
                  )}
                <div className="gridiconswrap">
                  <Tooltip placement="topLeft" title="Column Filter Options">
                    <Dropdown
                      overlay={columnMenu}
                      trigger={['click']}
                      placement="bottomRight"
                      visible={dropdownVisible}
                      onVisibleChange={setDropdownVisible}
                    >
                      <Button
                        type="primary"
                        icon={<ColumnList />}
                        onClick={e => e.preventDefault()}
                      />
                    </Dropdown>
                  </Tooltip>
                </div>
              </Space>
            </Col>
          </Row>
        </div>
      </div>
      <div className='tablewrp'>
        <Table
          rowKey="id"
          columns={filteredColumns}
          dataSource={data}
          pagination={false}
          loading={status === 'loading'}
          scroll={{ x: '600px' }}
          onChange={handleTableChange}
        />
      </div>
      <Pagination
        current={page}
        pageSize={pagesize}
        total={totalRecord}
        showSizeChanger
        onShowSizeChange={handlePageSizeChange}
        pageSizeOptions={['20', '50', '100']}
        showTotal={total => `Total ${total} items`}
        onChange={handlePageChange}
        locale={{ items_per_page: 'Items / Page' }}
      />
    </div>
  );
}
export default PlanningList;